import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Chatbot components
import Chatbot from './components/chatbot/Chatbot';
import ChatbotAdmin from './components/chatbot/ChatbotAdmin';

// PWA components
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';

// Layout components
import PublicLayout from './layouts/PublicLayout';

// Pages
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import FaqPage from './pages/FaqPage';
import TermsPage from './pages/TermsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ChatbotInfoPage from './pages/ChatbotInfoPage';
import EnhancedMineralsPage from './pages/EnhancedMineralsPage';
import DigitalTrustPage from './pages/DigitalTrustPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';

// Context providers
import { AuthProvider } from './context/AuthContext';

// Create theme based on Laprecio.com colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#ca8a04', // Gold/amber from Laprecio.com
      light: '#eab308',
      dark: '#a16207',
    },
    secondary: {
      main: '#eab308', // Lighter gold/yellow from Laprecio.com
      light: '#fcd34d',
      dark: '#b45309',
    },
    background: {
      default: '#fffef8', // Light cream background
      paper: '#FFFFFF',
      alternate: '#fffef0', // Light yellow for alternate sections
    },
    text: {
      primary: '#333333', // Dark gray for main text
      secondary: '#555555', // Medium gray for navigation
    },
    success: {
      main: '#10b981', // Teal green that complements the gold
    },
    error: {
      main: '#ef4444', // Red that complements the gold
    },
    hero: {
      gradient: 'linear-gradient(to right, #fffde7, #fff9c4)', // Hero section gradient
    },
  },
  typography: {
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    h1: {
      fontWeight: 700,
      color: '#333333',
    },
    h2: {
      fontWeight: 600,
      color: '#333333',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: '#333333',
    },
    h4: {
      fontWeight: 600,
      color: '#333333',
    },
    h5: {
      fontWeight: 600,
      color: '#333333',
    },
    body1: {
      color: '#333333',
      lineHeight: 1.6,
    },
    body2: {
      color: '#555555',
      lineHeight: 1.6,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 50, // Rounded buttons like on Laprecio.com
          textTransform: 'none',
          fontWeight: 700,
          padding: '0.75rem 2rem',
        },
        containedPrimary: {
          backgroundColor: '#eab308', // Use the lighter gold for buttons
          '&:hover': {
            backgroundColor: '#ca8a04',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '2px solid #f5e5a5',
          borderLeft: '6px solid #eab308',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          color: '#555555',
          borderBottom: '2px solid #eee',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#f5e5a5',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/minerals" element={<EnhancedMineralsPage />} />
              <Route path="/digital-trust" element={<DigitalTrustPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/chatbot-admin" element={<ChatbotAdmin />} />
              <Route path="/chatbot-info" element={<ChatbotInfoPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
            </Route>
          </Routes>
          <Chatbot />
          <PWAInstallPrompt />
          <OfflineIndicator />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
